'use client';

import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { emailLandingKind, recordEmailLanding } from '@/lib/email/landing';

/**
 * #3458: records a tracked email link once it lands after sign-in. Rendered
 * once in the root layout, which passes whether the request is signed in. A
 * signed-out landing (the branded login carrying the tagged URL in `next`)
 * records nothing; the redirect after sign-in lands on the tagged URL itself,
 * and that visit records the action.
 *
 * The search-params read sits under its own Suspense boundary with a null
 * fallback, like LearnerAccessBoundary's, so the root layout never wraps the
 * application in one (#2830).
 */
export function EmailLandingTracker({ signedIn }: { signedIn: boolean }) {
  return (
    <Suspense fallback={null}>
      <EmailLandingReader signedIn={signedIn} />
    </Suspense>
  );
}

function EmailLandingReader({ signedIn }: { signedIn: boolean }): null {
  const pathname = usePathname();
  const search = useSearchParams()?.toString() ?? '';

  useEffect(() => {
    if (!signedIn || !pathname) return;
    const kind = emailLandingKind(new URLSearchParams(search));
    if (kind) recordEmailLanding(kind, `${pathname}?${search}`);
  }, [signedIn, pathname, search]);

  return null;
}
